import React, { useState } from "react";
import { createUserWithEmailAndPassword, updateProfile, signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../../firebase";
import axios from "axios";
import Header from "../Header";
import Footer from "../Footer";
import "./Auth.css";

function Signup({ switchToLogin, onSignupSuccess }) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    profession: "General User"
  });
  const [consents, setConsents] = useState({
    termsOfService: false,
    privacyPolicy: false,
    marketingEmails: false,
    dataProcessing: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleConsentChange = (consentName) => {
    setConsents(prev => ({
      ...prev,
      [consentName]: !prev[consentName]
    }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    const { username, email, password } = formData;
    
    // Basic validation
    if (!username || !email || !password) {
      setError("Please fill in all required fields");
      return;
    }

    if (password.length < 8) {
      setError("Password should be at least 8 characters");
      return;
    }

    // GDPR consent validation
    if (!consents.termsOfService) {
      setError("You must accept the Terms of Service to continue");
      return;
    }

    if (!consents.privacyPolicy) {
      setError("You must accept the Privacy Policy to continue");
      return;
    }

    if (!consents.dataProcessing) {
      setError("You must consent to data processing to create an account");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      console.log("🔄 Starting signup process...");
      
      // 1️⃣ Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log("✅ Firebase user created:", userCredential.user.uid);
      
      // 2️⃣ Update profile with username
      await updateProfile(userCredential.user, { 
        displayName: username 
      });
      console.log("✅ Profile updated with username");
      
      // 3️⃣ Get token for backend
      const token = await userCredential.user.getIdToken();
      console.log("✅ Token obtained for backend");
      
      // 4️⃣ Send to backend for Firestore user creation with GDPR consents
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        email,
        username,
        profession: formData.profession,
        gdprConsents: {
          termsOfService: consents.termsOfService,
          privacyPolicy: consents.privacyPolicy,
          marketingEmails: consents.marketingEmails,
          dataProcessing: consents.dataProcessing,
          consentedAt: new Date().toISOString(),
          ipAddress: await getClientIP()
        }
      }, {
        headers: { 
          Authorization: `Bearer ${token}` 
        }
      });
      
      console.log("✅ Backend user created with GDPR consents:", res.data);
      
      // 5️⃣ Store token and user data (excluding sensitive consents)
      sessionStorage.setItem("token", token);
      const userDataToStore = { ...res.data.user };
      delete userDataToStore.gdprConsents; // Don't store consents in session storage
      sessionStorage.setItem("user", JSON.stringify(userDataToStore));
      
      onSignupSuccess(res.data.user);
      
    } catch (error) {
      console.error("❌ Signup failed:", error);
      
      // Handle specific error cases
      if (error.code === 'auth/email-already-in-use') {
        setError("This email is already registered. Please login instead.");
      } else if (error.code === 'auth/weak-password') {
        setError("Password is too weak. Please use a stronger password.");
      } else if (error.code === 'auth/invalid-email') {
        setError("Invalid email address.");
      } else if (error.code === 'auth/operation-not-allowed') {
        setError("Email/password sign-up is not enabled. Please contact support.");
      } else {
        setError(error.message || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("🔄 Attempting Google signup...");
      
      // Show GDPR consent modal for Google signup
      if (!consents.termsOfService || !consents.privacyPolicy || !consents.dataProcessing) {
        setError("Please accept the required consents before continuing with Google signup");
        setLoading(false);
        return;
      }
      
      // 1️⃣ Sign up with Google Popup
      const userCredential = await signInWithPopup(auth, googleProvider);
      console.log("✅ Google signup successful:", userCredential.user.uid);
      
      // 2️⃣ Get Firebase ID token
      const token = await userCredential.user.getIdToken();
      console.log("✅ Token obtained:", token.substring(0, 20) + "...");
      
      // 3️⃣ Send token to backend with GDPR consents
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, { 
        token,
        gdprConsents: {
          termsOfService: consents.termsOfService,
          privacyPolicy: consents.privacyPolicy,
          marketingEmails: consents.marketingEmails,
          dataProcessing: consents.dataProcessing,
          consentedAt: new Date().toISOString(),
          ipAddress: await getClientIP()
        }
      });
      
      console.log("✅ Backend signup response with GDPR:", res.data);
      
      // 4️⃣ Store token and user data
      sessionStorage.setItem("token", token);
      const userDataToStore = { ...res.data };
      delete userDataToStore.gdprConsents;
      sessionStorage.setItem("user", JSON.stringify(userDataToStore));
      
      onSignupSuccess(res.data);
      
    } catch (error) {
      console.error("❌ Google signup failed:", error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Google sign-up was cancelled.");
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError("This email is already registered. Please log in instead.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError("Sign-up popup was cancelled.");
      } else {
        setError("Google sign-up failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get client IP
  const getClientIP = async () => {
    try {
      const response = await axios.get('https://api.ipify.org?format=json');
      return response.data.ip;
    } catch (error) {
      console.error("Could not fetch IP:", error);
      return "unknown";
    }
  };

  // Modal components
  const PrivacyPolicyModal = () => (
    <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Privacy Policy</h3>
          <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
        </div>
        <div className="modal-body">
          <h4>Data Collection and Usage</h4>
          <p>We collect the following personal data when you sign up:</p>
          <ul>
            <li>Email address - for authentication and communication</li>
            <li>Username - for personalization</li>
            <li>Profession - to tailor your experience</li>
            <li>Consent records - for GDPR compliance</li>
          </ul>
          
          <h4>Your Rights Under GDPR</h4>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Withdraw consent at any time</li>
            <li>Data portability</li>
            <li>Object to data processing</li>
          </ul>
          
          <h4>Data Storage and Security</h4>
          <p>Your data is stored securely and encrypted. We implement security measures to protect your information.</p>
          
          <h4>Contact Information</h4>
          <p>For data-related inquiries, contact the addresses found on the bottom of the page.</p>
          <h4> </h4>
          <p> </p>
          <p> </p>
          
          <div className="modal-actions">
            <button 
              className="auth-button"
              onClick={() => {
                setShowPrivacyModal(false);
                handleConsentChange('privacyPolicy');
              }}
            >
              Accept Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const TermsOfServiceModal = () => (
    <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Terms of Service</h3>
          <button className="modal-close" onClick={() => setShowTermsModal(false)}>×</button>
        </div>
        <div className="modal-body">
          <h4>Acceptance of Terms</h4>
          <p>By creating an account, you agree to abide by these terms of service.</p>
          
          <h4>User Responsibilities</h4>
          <ul>
            <li>Provide accurate information during registration</li>
            <li>Maintain the confidentiality of your account</li>
            <li>Use the service in compliance with applicable laws</li>
            <li>Not engage in unauthorized access or use</li>
          </ul>
          
          <h4>Service Usage</h4>
          <p>Raider provides a platform for ai-integrated pdf reading. We reserve the right to modify or discontinue services at our discretion.</p>
          
          <h4>Limitation of Liability</h4>
          <p>Raider shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the service.</p>
          
          <h4> </h4>
          <p> </p>
          <p> </p>
          
          
          <div className="modal-actions">
            <button 
              className="auth-button"
              onClick={() => {
                setShowTermsModal(false);
                handleConsentChange('termsOfService');
              }}
            >
              Accept Terms of Service
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <div className="auth-background"></div>
      <div className="auth-content">
        <Header />
        <div className="auth-wrapper">
          <div className="auth-box">
            <h2>Create Account</h2>
            <p className="auth-subtitle">Join Raider today</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <button 
              type="button"
              onClick={handleGoogleSignup}
              disabled={loading}
              className="google-auth-button"
            >
              <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.5 24.5c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign up with Google
            </button>

            <div className="divider">
              <span>or</span>
            </div>
            
            <form onSubmit={handleSignup}>
              <div className="input-group">
                <input 
                  type="text" 
                  name="username"
                  placeholder="Full name" 
                  value={formData.username}
                  onChange={handleChange}
                  className="auth-input"
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="input-group">
                <input 
                  type="email" 
                  name="email"
                  placeholder="Email address" 
                  value={formData.email}
                  onChange={handleChange}
                  className="auth-input"
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="input-group">
                <input 
                  type="password" 
                  name="password"
                  placeholder="Password (min. 8 characters)" 
                  value={formData.password}
                  onChange={handleChange}
                  className="auth-input"
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="input-group">
                <select 
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  className="auth-input"
                  disabled={loading}
                >
                  <option value="General User">General User</option>
                  <option value="Student">Student</option>
                  <option value="Researcher">Researcher</option>
                  <option value="Professional">Professional</option>
                  <option value="Teacher">Teacher</option>
                  <option value="Developer">Developer</option>
                </select>
              </div>
              
              {/* GDPR Consent Section */}
              <div className="consent-section">
                <div className="consent-item required-consent">
                  <label className="consent-checkbox">
                    <input 
                      type="checkbox" 
                      checked={consents.termsOfService}
                      onChange={() => handleConsentChange('termsOfService')}
                      disabled={loading}
                    />
                    <span>
                      I accept the{" "}
                      <button 
                        type="button"
                        className="consent-link"
                        onClick={() => setShowTermsModal(true)}
                        disabled={loading}
                      >
                        Terms of Service
                      </button>
                    </span>
                  </label>
                </div>
                
                <div className="consent-item required-consent">
                  <label className="consent-checkbox">
                    <input 
                      type="checkbox" 
                      checked={consents.privacyPolicy}
                      onChange={() => handleConsentChange('privacyPolicy')}
                      disabled={loading}
                    />
                    <span>
                      I have read and agree to the{" "}
                      <button 
                        type="button"
                        className="consent-link"
                        onClick={() => setShowPrivacyModal(true)}
                        disabled={loading}
                      >
                        Privacy Policy
                      </button>
                    </span>
                  </label>
                </div>
                
                <div className="consent-item required-consent">
                  <label className="consent-checkbox">
                    <input 
                      type="checkbox" 
                      checked={consents.dataProcessing}
                      onChange={() => handleConsentChange('dataProcessing')}
                      disabled={loading}
                    />
                    <span>
                      I consent to the processing of my personal data for account creation and service provision
                    </span>
                  </label>
                </div>
                
                
                
                <p className="consent-note">
                  * You can withdraw consent at any time by contacting.
                </p>
              </div>
              
              <button 
                type="submit"
                disabled={loading}
                className="auth-button"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>
            
            <div className="auth-switch">
              <p>Already have an account?{" "}
                <button 
                  type="button"
                  onClick={switchToLogin} 
                  className="switch-link"
                  disabled={loading}
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
          
          {/* Modals */}
          {showPrivacyModal && <PrivacyPolicyModal />}
          {showTermsModal && <TermsOfServiceModal />}
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default Signup;